<script setup lang="ts">
import type { GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model';
import type { RenderFieldDisplayProps } from './types';
import { shallowRef, watch } from 'vue';
import { usePrimitiveField } from '~/composables/use-primitive-field';
import { useSingleRelationField } from '~/composables/use-single-relation-field';

const props = withDefaults(defineProps<RenderFieldDisplayProps>(), {
	root: true,
});

const primitiveField = usePrimitiveField(() => props.fieldId as GlobalPrimitiveFieldId);
const singleRelationField = useSingleRelationField(() => props.fieldId as GlobalSingleRelationFieldId);

/**
 * Keep a stable plain-object prop bag so recycled table rows do not force
 * child display components to diff on every parent scroll update.
 */
function createEffectiveProps(): RenderFieldDisplayProps {
	return {
		fieldId: props.fieldId,
		value: props.value,
		item: props.item,
		root: props.root,
	};
}

const effectiveProps = shallowRef<RenderFieldDisplayProps>(createEffectiveProps());

watch(() => [props.fieldId, props.value, props.item, props.root] as const, (newValue, oldValue) => {
	if (oldValue
		&& newValue[0] === oldValue[0]
		&& Object.is(newValue[1], oldValue[1])
		&& newValue[2] === oldValue[2]
		&& newValue[3] === oldValue[3]) {
		return;
	}

	effectiveProps.value = createEffectiveProps();
});
</script>

<template>
	<RenderPrimitiveFieldDisplay v-if="primitiveField" v-bind="effectiveProps" :field="primitiveField" />
	<RenderRelationFieldDisplay v-else-if="singleRelationField" v-bind="effectiveProps" :field="singleRelationField" />
</template>
